import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Printer, Loader2, Search, X, Save, History, RotateCcw, Copy, ArrowLeft, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { ShopNav } from "../components/shop/ShopNav";
import { ProductImage } from "../components/shop/ProductCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const collator = new Intl.Collator("nb", { numeric: true, sensitivity: "base" });

function sortForCount(list, sort) {
  if (sort === "name") return [...list].sort((a, b) => collator.compare(a.name || "", b.name || ""));
  return [...list].sort((a, b) => (!a.sku && b.sku ? 1 : a.sku && !b.sku ? -1 : collator.compare(a.sku || "", b.sku || "") || collator.compare(a.name || "", b.name || "")));
}

/** Avvik-merke: grønt når likt, raudt ved manko, gult/brand ved overskot */
const DiffBadge = ({ delta, t, testId }) => {
  if (delta === null) return <span className="text-muted-foreground" data-testid={testId}>–</span>;
  if (delta === 0) return <Badge className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30 hover:bg-[hsl(var(--success))]/10" data-testid={testId}>{t("Stemmer")}</Badge>;
  if (delta < 0) return <Badge className="bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border border-[hsl(var(--danger))]/30 hover:bg-[hsl(var(--danger))]/10 tabular-nums" data-testid={testId}>{delta}</Badge>;
  return <Badge className="bg-[hsl(var(--warning))]/15 text-foreground border border-[hsl(var(--warning))]/50 hover:bg-[hsl(var(--warning))]/15 tabular-nums" data-testid={testId}>+{delta}</Badge>;
};

/**
 * Lagerteljing: tel varene fysisk (gjerne med utskriven teljeliste), tast inn opptalt tal per vare, sjå avvik mot systemet
 * og registrer – lageret blir retta og avvika ført i lagerhistorikken som «Lagerteljing».
 */
export default function ShopStockCountPage() {
  const { t } = useLang();
  const [products, setProducts] = useState(null);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("sku");
  const [values, setValues] = useState({}); // product_id -> string (tomt = ikkje talt)
  const [note, setNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState("");
  const [result, setResult] = useState(null); // siste registrerte teljing (frå svaret)
  const inputRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([api.get("/my/shop/products"), api.get("/my/shop/stock-counts", { params: { limit: 10 } })]);
      setProducts(p.data.products);
      setCounts(c.data.counts);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(() => sortForCount(products || [], sort), [products, sort]);
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? sorted.filter((p) => `${p.name} ${p.sku || ""}`.toLowerCase().includes(needle)) : sorted;
  }, [sorted, q]);

  const parsed = (id) => {
    const v = (values[id] ?? "").trim();
    if (v === "") return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const rows = useMemo(() => (products || []).map((p) => {
    const counted = parsed(p.id);
    return { product: p, counted, delta: counted === null ? null : counted - (p.stock || 0) };
  }), [products, values]); // eslint-disable-line react-hooks/exhaustive-deps
  const countedRows = rows.filter((r) => r.counted !== null);
  const changedRows = countedRows.filter((r) => r.delta !== 0);
  const missing = changedRows.filter((r) => r.delta < 0).reduce((s, r) => s - r.delta, 0);
  const surplus = changedRows.filter((r) => r.delta > 0).reduce((s, r) => s + r.delta, 0);

  const setValue = (id, v) => {
    setResult(null);
    setValues((s) => ({ ...s, [id]: v.replace(/[^\d]/g, "").slice(0, 6) }));
  };
  const fillSystemForEmpty = () => {
    setValues((s) => {
      const next = { ...s };
      for (const p of products || []) if ((next[p.id] ?? "").trim() === "") next[p.id] = String(p.stock || 0);
      return next;
    });
  };
  const resetAll = () => { setValues({}); setResult(null); };

  // Enter i eit felt → hopp til neste vare i lista
  const onKeyDown = (e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = visible[idx + 1];
      if (next) inputRefs.current[next.id]?.focus();
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post("/my/shop/stock-count", { items: countedRows.map((r) => ({ product_id: r.product.id, counted: r.counted })), note: note.trim() });
      setResult(res.data.count);
      toast.success(t("Telling registrert: {counted} varer talt, {changed} med avvik", { counted: res.data.count?.counted ?? countedRows.length, changed: res.data.count?.changed ?? 0 }));
      setValues({});
      setNote("");
      setConfirmOpen(false);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const printList = async (mode) => {
    setPdfBusy(mode);
    try {
      const res = await api.get("/my/shop/stock-count.pdf", { params: { show_system: 1, sort }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      let opened = false;
      if (mode === "open") opened = !!window.open(url, "_blank");
      if (!opened) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "butikk-teljeliste.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lage PDF")));
    } finally {
      setPdfBusy("");
    }
  };

  // Vanleg funksjon (ikkje komponent) – elles ville feltet bli remonta ved kvart tastetrykk og miste fokus
  const renderCountInput = (p, idx, sfx = "") => (
    <Input
      ref={(el) => { if (!sfx) inputRefs.current[p.id] = el; }}
      inputMode="numeric" pattern="[0-9]*" placeholder="–" value={values[p.id] ?? ""} onChange={(e) => setValue(p.id, e.target.value)} onKeyDown={(e) => onKeyDown(e, idx)}
      aria-label={t("Opptalt antall for {name}", { name: p.name })} className="w-24 text-right tabular-nums font-medium" data-testid={`count-input-${p.id}${sfx}`}
    />
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="stock-count-page">
        <ShopNav />
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 text-muted-foreground" data-testid="stock-count-back"><Link to="/dashboard/butikk"><ArrowLeft className="w-4 h-4 mr-1" /> {t("Varer")}</Link></Button>
            <h2 className="text-lg font-heading font-semibold tracking-tight flex items-center gap-2"><ClipboardList className="w-5 h-5 text-muted-foreground" /> {t("Lagertelling")}</h2>
            <p className="text-sm text-muted-foreground max-w-2xl">{t("Skriv ut tellelisten, tell varene fysisk og tast inn opptalt antall her. Lageret rettes til det du har talt, og avvikene føres i lagerhistorikken som «Lagertelling».")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={!!pdfBusy || !products?.length} data-testid="stock-count-print-button">
                  {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Printer className="w-4 h-4 mr-1.5" />} {t("Skriv ut telleliste")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => printList("open")} data-testid="stock-count-print-open"><Printer className="w-4 h-4 mr-2" /> {t("Åpne og skriv ut")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => printList("download")} data-testid="stock-count-print-download"><Download className="w-4 h-4 mr-2" /> {t("Last ned PDF")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="stock-count-loading">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
        ) : error ? (
          <Card className="border-border"><CardContent className="p-8 text-center space-y-3">
            <p className="font-heading font-semibold">{t("Kunne ikke laste varene")}</p>
            <Button variant="outline" onClick={() => { setLoading(true); load(); }} data-testid="stock-count-retry">{t("Prøv igjen")}</Button>
          </CardContent></Card>
        ) : !products?.length ? (
          <Card className="border-dashed border-border" data-testid="stock-count-empty"><CardContent className="p-10 text-center space-y-3">
            <p className="font-heading font-semibold">{t("Ingen varer å telle ennå")}</p>
            <p className="text-sm text-muted-foreground">{t("Opprett varer i butikken først, så kan du telle dem her.")}</p>
            <Button asChild data-testid="stock-count-create-product"><Link to="/dashboard/butikk/ny">{t("Opprett vare")}</Link></Button>
          </CardContent></Card>
        ) : (
          <>
            {result && (
              <Card className="border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5" data-testid="stock-count-result">
                <CardContent className="p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))] mt-0.5" />
                    <div>
                      <p className="font-medium">{t("Telling registrert {time}", { time: formatDateTime(result.created_at) })}</p>
                      <p className="text-sm text-muted-foreground">{t("{counted} varer talt · {changed} med avvik · manko {missing} · overskudd {surplus}", { counted: result.counted, changed: result.changed, missing: result.missing, surplus: result.surplus })}{result.note ? ` · ${result.note}` : ""}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setResult(null)} data-testid="stock-count-result-dismiss"><X className="w-4 h-4 mr-1" /> {t("Lukk")}</Button>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk på navn eller varenummer")} className="pl-9 pr-8" aria-label={t("Søk på navn eller varenummer")} data-testid="stock-count-search" />
                  {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={t("Tøm søk")} data-testid="stock-count-search-clear"><X className="w-4 h-4" /></button>}
                </div>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="sm:w-44" aria-label={t("Sorter")} data-testid="stock-count-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sku">{t("Varenummer")}</SelectItem>
                    <SelectItem value="name">{t("Navn")} A–Å</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="ghost" size="sm" onClick={fillSystemForEmpty} title={t("Setter opptalt = systemtall for varer du ikke har fylt ut")} data-testid="stock-count-fill-system"><Copy className="w-4 h-4 mr-1.5" /> {t("Fyll tomme med systemtall")}</Button>
                <Button variant="ghost" size="sm" onClick={resetAll} disabled={countedRows.length === 0} data-testid="stock-count-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Nullstill")}</Button>
              </div>
            </div>

            {/* Desktop-tabell */}
            <Card className="border-border hidden md:block">
              <Table data-testid="stock-count-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Vare")}</TableHead>
                    <TableHead>{t("Varenr.")}</TableHead>
                    <TableHead className="text-right">{t("Lager (system)")}</TableHead>
                    <TableHead className="text-right">{t("Opptalt")}</TableHead>
                    <TableHead>{t("Avvik")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="stock-count-no-hits">{t("Ingen varer passet søket")}</TableCell></TableRow>
                  )}
                  {visible.map((p, idx) => {
                    const r = rows.find((x) => x.product.id === p.id);
                    return (
                      <TableRow key={p.id} className={r?.counted !== null ? (r.delta === 0 ? "" : "bg-[hsl(var(--warning))]/5") : ""} data-testid={`stock-count-row-${p.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="w-12 h-9 rounded-md overflow-hidden bg-muted shrink-0"><ProductImage product={p} /></span>
                            <span className="min-w-0">
                              <span className="block font-medium truncate">{p.name}</span>
                              {!p.active && <span className="text-xs text-muted-foreground">{t("Skjult")}</span>}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">{p.sku || "–"}</TableCell>
                        <TableCell className="text-right tabular-nums" data-testid={`stock-count-system-${p.id}`}>{p.stock}</TableCell>
                        <TableCell className="text-right"><div className="flex justify-end">{renderCountInput(p, idx)}</div></TableCell>
                        <TableCell><DiffBadge delta={r?.delta ?? null} t={t} testId={`stock-count-diff-${p.id}`} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
            {/* Mobil-kort */}
            <div className="space-y-2 md:hidden" data-testid="stock-count-card-list">
              {visible.length === 0 && <Card className="border-dashed border-border"><CardContent className="p-6 text-center text-sm text-muted-foreground">{t("Ingen varer passet søket")}</CardContent></Card>}
              {visible.map((p, idx) => {
                const r = rows.find((x) => x.product.id === p.id);
                return (
                  <Card key={p.id} className="border-border" data-testid={`stock-count-card-${p.id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <span className="w-14 h-12 rounded-md overflow-hidden bg-muted shrink-0"><ProductImage product={p} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug line-clamp-2">{p.name}</p>
                        <p className="text-xs text-muted-foreground tabular-nums">{p.sku && <span className="font-mono mr-2">{p.sku}</span>}{t("System")}: {p.stock}</p>
                        <div className="mt-1"><DiffBadge delta={r?.delta ?? null} t={t} testId={`stock-count-diff-${p.id}-m`} /></div>
                      </div>
                      {renderCountInput(p, idx, "-m")}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Oppsummering + lagre */}
            <Card className="border-border sticky bottom-3 z-10 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/90" data-testid="stock-count-summary-bar">
              <CardContent className="p-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-2 flex-1">
                  <p className="text-sm" data-testid="stock-count-summary">
                    <strong className="tabular-nums">{countedRows.length}</strong> {t("av")} <strong className="tabular-nums">{products.length}</strong> {t("varer talt")}
                    {countedRows.length > 0 && <> · <span className="tabular-nums">{changedRows.length}</span> {t("med avvik")}{missing > 0 && <> · {t("manko")} <span className="text-[hsl(var(--danger))] tabular-nums font-medium">−{missing}</span></>}{surplus > 0 && <> · {t("overskudd")} <span className="tabular-nums font-medium">+{surplus}</span></>}</>}
                  </p>
                  <div className="space-y-1 max-w-md">
                    <Label htmlFor="count-note" className="text-xs text-muted-foreground">{t("Merknad (valgfritt)")}</Label>
                    <Input id="count-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder={t("F.eks. «Telling før julemarkedet»")} data-testid="stock-count-note" />
                  </div>
                </div>
                <Button onClick={() => setConfirmOpen(true)} disabled={countedRows.length === 0 || saving} data-testid="stock-count-save-button">
                  <Save className="w-4 h-4 mr-1.5" /> {t("Registrer telling")}{countedRows.length > 0 ? ` (${countedRows.length})` : ""}
                </Button>
              </CardContent>
            </Card>

            {/* Siste teljingar */}
            <Card className="border-border" data-testid="stock-count-history">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2"><History className="w-4 h-4 text-muted-foreground" /> {t("Siste tellinger")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!counts?.length ? (
                  <p className="p-4 text-sm text-muted-foreground" data-testid="stock-count-history-empty">{t("Ingen tellinger registrert ennå.")}</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {counts.map((c) => (
                      <li key={c.id} className="p-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-sm" data-testid={`stock-count-history-${c.id}`}>
                        <div>
                          <p className="font-medium">{formatDateTime(c.created_at)}{c.note ? <span className="font-normal text-muted-foreground"> · {c.note}</span> : null}</p>
                          <p className="text-xs text-muted-foreground">{c.actor}</p>
                        </div>
                        <p className="text-muted-foreground tabular-nums">{t("{counted} talt · {changed} avvik", { counted: c.counted, changed: c.changed })}{c.missing > 0 ? ` · −${c.missing}` : ""}{c.surplus > 0 ? ` · +${c.surplus}` : ""}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="stock-count-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{t("Registrere tellingen?")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>{changedRows.length === 0
                  ? t("{n} varer talt – alt stemmer med systemet. Ingen lagertall endres, men tellingen lagres i historikken.", { n: countedRows.length })
                  : t("{n} varer talt. Lageret på {changed} varer rettes til opptalt antall, og avvikene føres i lagerhistorikken.", { n: countedRows.length, changed: changedRows.length })}</p>
                {changedRows.length > 0 && (
                  <ul className="max-h-56 overflow-auto rounded-lg border border-border divide-y divide-border" data-testid="stock-count-confirm-list">
                    {changedRows.map((r) => (
                      <li key={r.product.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="truncate text-foreground">{r.product.name}{r.product.sku && <span className="ml-2 font-mono text-xs text-muted-foreground">{r.product.sku}</span>}</span>
                        <span className="tabular-nums whitespace-nowrap">{r.product.stock} → <strong className="text-foreground">{r.counted}</strong> <span className={r.delta < 0 ? "text-[hsl(var(--danger))]" : ""}>({r.delta > 0 ? "+" : ""}{r.delta})</span></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stock-count-confirm-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); save(); }} disabled={saving} data-testid="stock-count-confirm-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {t("Registrer telling")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
